/**
 * Signing Status Display - Consumer Portal
 *
 * Displays the current status of a signing request with visual indicators.
 * Shows overall status and individual signer progress.
 *
 * Issue #300: E-Sign service integration with DocuSeal
 */

import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, GlassChip, GlassTooltip, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { EsignSubmissionStatus, EsignSubmitterStatus } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';

interface Submitter {
  id: number;
  email: string;
  name?: string;
  role?: string;
  status: EsignSubmitterStatus;
  completed_at?: string | null;
}

interface SigningStatusDisplayProps {
  status: EsignSubmissionStatus;
  submitters?: Submitter[];
  compact?: boolean;
  showSubmitters?: boolean;
}

// Status config with translation keys
const STATUS_CONFIG = {
  pending: {
    labelKey: 'signing.status.pending',
    color: 'warning' as const,
    icon: <PendingIcon fontSize="small" />,
  },
  completed: {
    labelKey: 'signing.status.completed',
    color: 'success' as const,
    icon: <CheckIcon fontSize="small" />,
  },
  expired: {
    labelKey: 'signing.status.expired',
    color: 'error' as const,
    icon: <CancelIcon fontSize="small" />,
  },
  archived: {
    labelKey: 'signing.status.archived',
    color: 'default' as const,
    icon: <ScheduleIcon fontSize="small" />,
  },
};

const SUBMITTER_STATUS_CONFIG = {
  pending: { labelKey: 'signing.submitter.pending', color: 'default' as const },
  sent: { labelKey: 'signing.submitter.sent', color: 'info' as const },
  opened: { labelKey: 'signing.submitter.opened', color: 'warning' as const },
  completed: { labelKey: 'signing.submitter.completed', color: 'success' as const },
};

export const SigningStatusDisplay = ({
  status,
  submitters = [],
  compact = false,
  showSubmitters = true,
}: SigningStatusDisplayProps): ReactElement => {
  const { t } = useI18n();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const label = t(config.labelKey);

  if (compact) {
    return (
      <GlassTooltip
        title={`${label} - ${t('signing.signedCount', { completed: submitters.filter((s) => s.status === 'completed').length, total: submitters.length })}`}
      >
        <GlassChip label={label} color={config.color} size="small" icon={config.icon} />
      </GlassTooltip>
    );
  }

  const completedCount = submitters.filter((s) => s.status === 'completed').length;

  return (
    <Box>
      {/* Overall Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showSubmitters && submitters.length > 0 ? 2 : 0 }}>
        <GlassChip label={label} color={config.color} icon={config.icon} />
        {submitters.length > 0 && (
          <Typography variant="body2" color="textSecondary">
            {t('signing.signedCount', { completed: completedCount, total: submitters.length })}
          </Typography>
        )}
      </Box>

      {/* Individual Submitters */}
      {showSubmitters && submitters.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {submitters.map((submitter) => {
            const submitterConfig = SUBMITTER_STATUS_CONFIG[submitter.status] || SUBMITTER_STATUS_CONFIG.pending;
            const submitterLabel = t(submitterConfig.labelKey);
            return (
              <Box
                key={submitter.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {submitter.name || submitter.email}
                  </Typography>
                  {submitter.role && (
                    <Typography variant="caption" color="textSecondary">
                      {submitter.role}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GlassChip label={submitterLabel} color={submitterConfig.color} size="small" />
                  {submitter.completed_at && (
                    <Typography variant="caption" color="textSecondary">
                      {new Date(submitter.completed_at).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SigningStatusDisplay;
